import React from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import { CameraAlt, AutoAwesome, Public } from '@mui/icons-material';

const ImpactTagline = ({ variant = 'hero', animated = true }) => {
    const theme = useTheme();

    const iconSize = variant === 'hero' ? { xs: 24, sm: 28, md: 32 } : { xs: 20, sm: 22, md: 24 };
    const fontSize = variant === 'hero'
        ? { xs: '1.5rem', sm: '2.2rem', md: '2.8rem' }
        : { xs: '1rem', sm: '1.4rem', md: '1.6rem' };

    const steps = [
        {
            text: 'Snap it',
            icon: <CameraAlt sx={{ fontSize: iconSize, color: '#2196f3' }} />,
            color: '#2196f3'
        },
        {
            text: 'Sort it',
            icon: <AutoAwesome sx={{ fontSize: iconSize, color: '#ff9800' }} />,
            color: '#ff9800'
        },
        {
            text: 'Save the Planet',
            icon: <Public sx={{ fontSize: iconSize, color: '#4caf50' }} />,
            color: '#4caf50'
        }
    ];

    if (variant === 'compact') {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    mx: { xs: 1, sm: 0 },
                    bgcolor: 'rgba(67, 160, 71, 0.05)',
                    borderRadius: { xs: 2, sm: 3 },
                    border: '2px solid',
                    borderColor: 'primary.light',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': animated ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'shimmer 3s infinite',
                        '@keyframes shimmer': {
                            '0%': { left: '-100%' },
                            '100%': { left: '100%' }
                        }
                    } : {}
                }}
            >
                <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                        background: 'linear-gradient(90deg, #1b5e20, #43a047, #66bb6a)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundSize: '200% 100%',
                        animation: animated ? 'gradient 3s ease infinite' : 'none',
                        '@keyframes gradient': {
                            '0%, 100%': { backgroundPosition: '0% 50%' },
                            '50%': { backgroundPosition: '100% 50%' }
                        },
                        fontSize: fontSize,
                        mb: 0.5,
                        lineHeight: { xs: 1.2, sm: 1.3 }
                    }}
                >
                    {/* Show simplified text on very small screens */}
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Snap it → Sort it → Save the Bangladesh
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Snap • Sort • Save
                    </Box>
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                >
                    Your journey to smarter waste management
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                textAlign: 'center',
                py: { xs: 3, sm: 4, md: 6 },
                px: { xs: 1, sm: 2 },
                position: 'relative'
            }}
        >
            <Chip
                label="Simple 3-Step Process"
                color="primary"
                size="small"
                sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.9rem' },
                    height: { xs: 28, sm: 30, md: 32 }
                }}
            />

            <Typography
                variant="h2"
                fontWeight={800}
                gutterBottom
                sx={{
                    fontSize: fontSize,
                    background: 'linear-gradient(90deg, #1b5e20, #43a047, #66bb6a)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '300% 100%',
                    animation: animated ? 'gradientShift 4s ease-in-out infinite' : 'none',
                    '@keyframes gradientShift': {
                        '0%, 100%': { backgroundPosition: '0% 50%' },
                        '50%': { backgroundPosition: '100% 50%' }
                    },
                    mb: { xs: 1.5, sm: 2 },
                    textShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    lineHeight: { xs: 1.1, sm: 1.2, md: 1.3 }
                }}
            >
                {/* Responsive text display */}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Snap it → Sort it → Save the Planet
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Snap it<br />Sort it<br />Save the Planet
                </Box>
            </Typography>

            {/* Interactive Step Visualization */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, sm: 2, md: 4 },
                    mt: { xs: 3, sm: 4 },
                    mb: { xs: 2, sm: 3 }
                }}
            >
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: { xs: 0.5, sm: 1 },
                                animation: animated ? `fadeInUp 0.6s ease ${index * 0.2}s both` : 'none',
                                '@keyframes fadeInUp': {
                                    'from': {
                                        opacity: 0,
                                        transform: 'translateY(30px)'
                                    },
                                    'to': {
                                        opacity: 1,
                                        transform: 'translateY(0)'
                                    }
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 50, sm: 60, md: 80 },
                                    height: { xs: 50, sm: 60, md: 80 },
                                    borderRadius: '50%',
                                    bgcolor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: `0 8px 24px ${step.color}20`,
                                    border: `3px solid ${step.color}`,
                                    transition: 'all 0.3s ease',
                                    '&:hover': animated ? {
                                        transform: { xs: 'translateY(-2px)', sm: 'translateY(-5px)' },
                                        boxShadow: `0 12px 32px ${step.color}30`
                                    } : {}
                                }}
                            >
                                {step.icon}
                            </Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                    color: step.color,
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.2rem' }
                                }}
                            >
                                {step.text}
                            </Typography>
                        </Box>

                        {index < steps.length - 1 && (
                            <Typography
                                variant="h4"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 300,
                                    opacity: 0.6,
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: { sm: '1.5rem', md: '2rem' },
                                    animation: animated ? `pulse 2s ease-in-out infinite ${index * 0.3}s` : 'none',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 0.6 },
                                        '50%': { opacity: 1 }
                                    }
                                }}
                            >
                                →
                            </Typography>
                        )}
                    </React.Fragment>
                ))}
            </Box>

            <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                    maxWidth: { xs: 300, sm: 500, md: 600 },
                    mx: 'auto',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    lineHeight: { xs: 1.5, sm: 1.6 },
                    px: { xs: 1, sm: 0 },
                    animation: animated ? 'fadeIn 0.8s ease 0.6s both' : 'none',
                    '@keyframes fadeIn': {
                        'from': { opacity: 0 },
                        'to': { opacity: 1 }
                    }
                }}
            >
                Transform your daily waste disposal into an impactful environmental action.
                Every photo you take, every classification you make, contributes to a cleaner, greener future.
            </Typography>
        </Box>
    );
};

export default ImpactTagline;
